import React, { useState, useEffect } from 'react';
import { Plus, Check, Edit2, Trash2, Lock, Shield, Sparkles, X } from 'lucide-react';
import { api, type UserProfile, type CreateProfileDto } from '../lib/api';
import { useThemeLanguage } from '../context/ThemeLanguageContext';

export interface ProfilePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProfile: (profile: UserProfile) => void;
  activeProfileId?: string;
}

const AVATAR_PRESETS = [
  { id: 'avatar-red', bg: 'from-red-600 to-rose-900', label: '🍿', name: 'Popcorn Red' },
  { id: 'avatar-blue', bg: 'from-blue-600 to-cyan-900', label: '🎬', name: 'Cinema Blue' },
  { id: 'avatar-purple', bg: 'from-purple-600 to-indigo-900', label: '🚀', name: 'Sci-Fi Purple' },
  { id: 'avatar-emerald', bg: 'from-emerald-500 to-teal-900', label: '🦁', name: 'Jungle Green' },
  { id: 'avatar-amber', bg: 'from-amber-500 to-orange-800', label: '⭐', name: 'Star Gold' },
  { id: 'avatar-pink', bg: 'from-pink-500 to-fuchsia-900', label: '🦄', name: 'Magic Pink' },
  { id: 'avatar-kids', bg: 'from-yellow-400 to-amber-600', label: '🧸', name: 'Kids Fun' },
  { id: 'avatar-dark', bg: 'from-neutral-700 to-neutral-900', label: '🕶️', name: 'Noir Dark' },
];

export function ProfilePickerModal({
  isOpen,
  onClose,
  onSelectProfile,
  activeProfileId,
}: ProfilePickerModalProps) {
  const { t, language, showToast } = useThemeLanguage();
  const [profiles, setProfiles] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [isManaging, setIsManaging] = useState(false);

  // Add / Edit Modal state
  const [editingProfile, setEditingProfile] = useState<UserProfile | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formName, setFormName] = useState('');
  const [formAvatar, setFormAvatar] = useState('avatar-red');
  const [formIsKids, setFormIsKids] = useState(false);
  const [formPin, setFormPin] = useState('');

  // PIN Verification Modal state
  const [pinTargetProfile, setPinTargetProfile] = useState<UserProfile | null>(null);
  const [enteredPin, setEnteredPin] = useState('');
  const [pinError, setPinError] = useState(false);

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const list = await api.getProfiles();
      setProfiles(list);
    } catch (err) {
      console.error('Failed to load profiles', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchProfiles();
      setIsManaging(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleProfileClick = (profile: UserProfile) => {
    if (isManaging) {
      // Open editor
      setEditingProfile(profile);
      setFormName(profile.name);
      setFormAvatar(profile.avatar || 'avatar-red');
      setFormIsKids(profile.isKids);
      setFormPin('');
      setShowEditModal(true);
      return;
    }

    if (profile.hasPin) {
      // Require PIN unlock
      setPinTargetProfile(profile);
      setEnteredPin('');
      setPinError(false);
    } else {
      chooseProfile(profile);
    }
  };

  const chooseProfile = (profile: UserProfile) => {
    localStorage.setItem('omflix_active_profile', JSON.stringify(profile));
    onSelectProfile(profile);
    showToast(
      language === 'he' ? `שלום, ${profile.name}!` : `Welcome, ${profile.name}!`,
      'success',
    );
    onClose();
  };

  const handleVerifyPin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pinTargetProfile) return;

    try {
      const res = await api.verifyProfilePin(pinTargetProfile.id, enteredPin);
      if (res.valid) {
        chooseProfile(pinTargetProfile);
        setPinTargetProfile(null);
      } else {
        setPinError(true);
        setEnteredPin('');
      }
    } catch {
      setPinError(true);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;

    try {
      if (editingProfile) {
        await api.updateProfile(editingProfile.id, {
          name: formName.trim(),
          avatar: formAvatar,
          isKids: formIsKids,
          pin: formPin ? formPin.trim() : undefined,
        });
        showToast(language === 'he' ? 'הפרופיל עודכן בהצלחה' : 'Profile updated successfully', 'success');
      } else {
        await api.createProfile({
          name: formName.trim(),
          avatar: formAvatar,
          isKids: formIsKids,
          pin: formPin ? formPin.trim() : undefined,
        });
        showToast(language === 'he' ? 'פרופיל חדש נוצר בהצלחה' : 'New profile created', 'success');
      }

      setShowEditModal(false);
      setEditingProfile(null);
      await fetchProfiles();
    } catch (err: any) {
      showToast(err.message || 'Error saving profile', 'error');
    }
  };

  const handleDeleteProfile = async (id: string) => {
    if (profiles.length <= 1) {
      showToast(language === 'he' ? 'לא ניתן למחוק את הפרופיל האחרון' : 'Cannot delete last profile', 'error');
      return;
    }
    if (!confirm(language === 'he' ? 'האם למחוק פרופיל זה?' : 'Delete this profile?')) return;

    try {
      await api.deleteProfile(id);
      showToast(language === 'he' ? 'הפרופיל נמחק' : 'Profile deleted', 'success');
      setShowEditModal(false);
      setEditingProfile(null);
      await fetchProfiles();
    } catch (err: any) {
      showToast(err.message || 'Error deleting profile', 'error');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-2xl flex items-center justify-center p-4 animate-in fade-in select-none">
      {/* Background Cinematic Scrim */}
      <div className="absolute inset-0 bg-radial from-red-950/20 via-black to-black pointer-events-none" />

      <div className="relative z-10 w-full max-w-4xl text-center">
        <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight drop-shadow-lg mb-3">
          {language === 'he' ? 'מי צופה עכשיו?' : "Who's watching?"}
        </h1>
        <p className="text-sm sm:text-base text-neutral-400 mb-8 sm:mb-12">
          {language === 'he'
            ? 'בחר פרופיל כדי לקבל המלצות מותאמות אישית, היסטוריה ורשימת צפייה'
            : 'Select your profile for personalized recommendations, history, and watchlist'}
        </p>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#E50914]" />
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 mb-12">
            {profiles.map((profile) => {
              const avatarPreset =
                AVATAR_PRESETS.find((a) => a.id === profile.avatar) || AVATAR_PRESETS[0];
              const isCurrent = profile.id === activeProfileId;

              return (
                <div
                  key={profile.id}
                  onClick={() => handleProfileClick(profile)}
                  className="group flex flex-col items-center cursor-pointer transition-all duration-300"
                >
                  <div
                    className={`relative w-24 h-24 sm:w-36 sm:h-36 rounded-2xl sm:rounded-3xl bg-gradient-to-br ${avatarPreset?.bg} p-1 shadow-2xl transition-all duration-300 group-hover:scale-105 group-hover:shadow-[0_0_30px_rgba(229,9,20,0.5)] group-active:scale-95 flex items-center justify-center border-2 ${
                      isCurrent ? 'border-white ring-4 ring-red-500/50' : 'border-transparent group-hover:border-white'
                    }`}
                  >
                    <span className="text-4xl sm:text-6xl select-none filter drop-shadow-md">
                      {avatarPreset?.label}
                    </span>

                    {/* Kids Badge */}
                    {profile.isKids && (
                      <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-amber-400 text-black font-black text-[10px] tracking-wider uppercase shadow-md">
                        KIDS
                      </span>
                    )}

                    {/* PIN Lock Icon */}
                    {profile.hasPin && (
                      <span className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white backdrop-blur-md">
                        <Lock className="w-3.5 h-3.5 text-amber-300" />
                      </span>
                    )}

                    {/* Edit Overlay if in Manage Mode */}
                    {isManaging && (
                      <div className="absolute inset-0 rounded-2xl sm:rounded-3xl bg-black/60 backdrop-blur-sm flex items-center justify-center text-white animate-in fade-in">
                        <Edit2 className="w-8 h-8 text-white group-hover:scale-110 transition-transform" />
                      </div>
                    )}
                  </div>

                  <span className="mt-3 text-sm sm:text-base font-bold text-neutral-300 group-hover:text-white transition-colors truncate max-w-[120px]">
                    {profile.name}
                  </span>
                </div>
              );
            })}

            {/* Add Profile Tile */}
            {profiles.length < 8 && !isManaging && (
              <div
                onClick={() => {
                  setEditingProfile(null);
                  setFormName('');
                  setFormAvatar('avatar-blue');
                  setFormIsKids(false);
                  setFormPin('');
                  setShowEditModal(true);
                }}
                className="group flex flex-col items-center cursor-pointer transition-all duration-300"
              >
                <div className="w-24 h-24 sm:w-36 sm:h-36 rounded-2xl sm:rounded-3xl border-2 border-dashed border-neutral-700 group-hover:border-white group-hover:bg-white/5 flex items-center justify-center transition-all duration-300 group-hover:scale-105 group-active:scale-95">
                  <Plus className="w-10 h-10 text-neutral-500 group-hover:text-white transition-colors" />
                </div>
                <span className="mt-3 text-sm sm:text-base font-bold text-neutral-500 group-hover:text-white transition-colors">
                  {language === 'he' ? 'הוסף פרופיל' : 'Add Profile'}
                </span>
              </div>
            )}

            {/* Guest Mode Tile */}
            {!isManaging && (
              <div
                onClick={() => {
                  const guestProfile: UserProfile = {
                    id: 'guest',
                    userId: 'guest',
                    name: language === 'he' ? 'אורח' : 'Guest',
                    avatar: 'avatar-dark',
                    isKids: false,
                    hasPin: false,
                    ageRating: 'PG-13',
                    language: 'he',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString(),
                  };
                  chooseProfile(guestProfile);
                }}
                className="group flex flex-col items-center cursor-pointer transition-all duration-300"
              >
                <div className="w-24 h-24 sm:w-36 sm:h-36 rounded-2xl sm:rounded-3xl bg-neutral-900 border-2 border-dashed border-white/20 group-hover:border-amber-400 group-hover:bg-amber-500/10 flex flex-col items-center justify-center transition-all duration-300 group-hover:scale-105 group-active:scale-95 shadow-xl">
                  <span className="text-3xl sm:text-5xl filter drop-shadow">🎭</span>
                  <span className="mt-1 text-[10px] sm:text-xs font-bold text-amber-400 uppercase tracking-wider">
                    {language === 'he' ? 'אורח' : 'Guest'}
                  </span>
                </div>
                <span className="mt-3 text-xs sm:text-sm font-semibold text-neutral-400 group-hover:text-white transition-colors">
                  {language === 'he' ? 'צפייה כאורח' : 'Guest Mode'}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Bottom Actions */}
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={() => setIsManaging(!isManaging)}
            className={`px-7 py-2.5 rounded-xl border text-sm font-bold tracking-wider uppercase transition-all duration-200 cursor-pointer ${
              isManaging
                ? 'bg-white text-black border-white shadow-xl hover:bg-neutral-200'
                : 'border-neutral-700 text-neutral-400 hover:text-white hover:border-white bg-black/40'
            }`}
          >
            {isManaging
              ? language === 'he' ? 'סיום ניהול' : 'Done'
              : language === 'he' ? 'ניהול פרופילים' : 'Manage Profiles'}
          </button>

          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl text-neutral-500 hover:text-neutral-300 text-sm font-semibold transition-colors cursor-pointer"
          >
            {language === 'he' ? 'סגור' : 'Cancel'}
          </button>
        </div>
      </div>

      {/* ─── Create / Edit Profile Modal ─── */}
      {showEditModal && (
        <div className="fixed inset-0 z-60 bg-black/85 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in" dir="rtl">
          <div className="bg-neutral-950 border border-white/20 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl relative">
            <button
              onClick={() => setShowEditModal(false)}
              className="absolute top-6 left-6 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-2xl font-black text-white mb-6">
              {editingProfile
                ? language === 'he' ? 'עריכת פרופיל' : 'Edit Profile'
                : language === 'he' ? 'הוספת פרופיל חדש' : 'Add New Profile'}
            </h2>

            <form onSubmit={handleSaveProfile} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">
                  {language === 'he' ? 'שם הפרופיל' : 'Profile Name'}
                </label>
                <input
                  type="text"
                  required
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder={language === 'he' ? 'לדוגמה: אבא, אמא, דני...' : 'e.g. Mom, Dad, Alex...'}
                  className="w-full px-4 py-3 rounded-xl bg-neutral-900 border border-white/15 text-white font-semibold focus:outline-none focus:border-[#E50914] transition-colors"
                />
              </div>

              {/* Avatar Selector */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">
                  {language === 'he' ? 'בחר אוואטר' : 'Choose Avatar'}
                </label>
                <div className="grid grid-cols-4 gap-2.5">
                  {AVATAR_PRESETS.map((preset) => (
                    <button
                      type="button"
                      key={preset.id}
                      onClick={() => setFormAvatar(preset.id)}
                      className={`h-16 rounded-xl bg-gradient-to-br ${preset.bg} flex items-center justify-center text-2xl transition-all ${
                        formAvatar === preset.id ? 'ring-4 ring-white scale-105 shadow-xl' : 'opacity-70 hover:opacity-100'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Kids Profile Switch */}
              <div className="flex items-center justify-between p-3.5 rounded-xl bg-neutral-900 border border-white/10">
                <div>
                  <h4 className="text-sm font-bold text-white">
                    {language === 'he' ? 'פרופיל ילדים?' : 'Kids Profile?'}
                  </h4>
                  <p className="text-xs text-neutral-400">
                    {language === 'he' ? 'הצגת תכנים מותאמים לכל המשפחה בלבד' : 'Family and kids friendly content only'}
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={formIsKids}
                  onChange={(e) => setFormIsKids(e.target.checked)}
                  className="w-5 h-5 accent-[#E50914] cursor-pointer"
                />
              </div>

              {/* Optional 4-digit PIN */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">
                  {language === 'he' ? 'קוד נעילה (PIN - אופציונלי)' : 'Security PIN (Optional)'}
                </label>
                <input
                  type="password"
                  maxLength={4}
                  value={formPin}
                  onChange={(e) => setFormPin(e.target.value.replace(/\D/g, ''))}
                  placeholder="4 ספרות להגנה מפני גישה"
                  className="w-full px-4 py-3 rounded-xl bg-neutral-900 border border-white/15 text-white font-mono text-center tracking-widest text-lg focus:outline-none focus:border-[#E50914] transition-colors"
                />
              </div>

              {/* Buttons */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 py-3.5 rounded-xl bg-[#E50914] hover:bg-[#b80710] text-white font-bold text-sm shadow-xl shadow-red-950/50 transition-all cursor-pointer"
                >
                  {language === 'he' ? 'שמור פרופיל' : 'Save Profile'}
                </button>

                {editingProfile && (
                  <button
                    type="button"
                    onClick={() => handleDeleteProfile(editingProfile.id)}
                    className="p-3.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 transition-colors cursor-pointer"
                    title="מחק פרופיל"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── PIN Unlock Modal ─── */}
      {pinTargetProfile && (
        <div className="fixed inset-0 z-60 bg-black/90 backdrop-blur-xl flex items-center justify-center p-4 animate-in fade-in" dir="rtl">
          <div className="bg-neutral-950 border border-white/20 rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl text-center relative">
            <button
              onClick={() => setPinTargetProfile(null)}
              className="absolute top-6 left-6 p-2 rounded-full hover:bg-white/10 text-neutral-400 hover:text-white transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center mx-auto mb-4 text-amber-400">
              <Lock className="w-8 h-8" />
            </div>

            <h3 className="text-xl font-black text-white mb-1">
              {language === 'he' ? 'פרופיל נעול' : 'Locked Profile'}
            </h3>
            <p className="text-xs text-neutral-400 mb-6">
              {language === 'he'
                ? `הזן את קוד ה-PIN עבור ${pinTargetProfile.name}`
                : `Enter PIN for ${pinTargetProfile.name}`}
            </p>

            <form onSubmit={handleVerifyPin} className="space-y-4">
              <input
                type="password"
                maxLength={4}
                autoFocus
                value={enteredPin}
                onChange={(e) => {
                  setPinError(false);
                  setEnteredPin(e.target.value.replace(/\D/g, ''));
                }}
                placeholder="••••"
                className={`w-40 mx-auto px-4 py-3 rounded-2xl bg-neutral-900 border text-center font-mono text-2xl tracking-[0.5em] text-white focus:outline-none transition-all ${
                  pinError ? 'border-red-500 ring-2 ring-red-500/50' : 'border-white/20 focus:border-[#E50914]'
                }`}
              />

              {pinError && (
                <p className="text-xs text-red-400 font-bold">
                  {language === 'he' ? 'קוד ה-PIN שגוי, נסה שוב' : 'Incorrect PIN, try again'}
                </p>
              )}

              <button
                type="submit"
                disabled={enteredPin.length < 4}
                className="w-full py-3 rounded-xl bg-[#E50914] hover:bg-[#b80710] disabled:opacity-40 text-white font-bold text-sm shadow-xl transition-all cursor-pointer"
              >
                {language === 'he' ? 'פתח פרופיל' : 'Unlock Profile'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
